import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { getErrorMessage } from '../utils/errorMessages';
import PhoneInput from './PhoneInput';
import { PRICE_ENTRY_MODE, resolvePriceBreakdown } from '../utils/vatPricing';

/**
 * The VAT rates the form offers. 5% and 10% are not Saudi rates and are no longer listed;
 * an existing booking outside this list is still shown its own rate — see the select below.
 */
const STANDARD_VAT_OPTIONS = [15, 0];

const EditBookingModal = ({ show, onHide, booking, onBookingUpdated }) => {
  const [formData, setFormData] = useState({
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    checkIn: '',
    checkOut: '',
    numberOfGuests: 1,
    // Same two bases as the create modal. Default is the stored VAT-INCLUSIVE total, so
    // editing a phone number re-submits the agreed figure UNTOUCHED — recomputing it from
    // the subtotal moved a 3,000.00 booking to 3,000.01 on every save.
    priceEntryMode: PRICE_ENTRY_MODE.INCLUSIVE_TOTAL,
    stayTotalWithVat: 0,  // Total for the stay, VAT included
    subtotal: 0,  // Stay price BEFORE VAT (the other basis)
    vatPercentage: 15,  // Default 15% VAT
    notes: '',
    roomTypeId: '',
    ratePlanId: ''
  });

  // Whichever basis is active, resolved ONCE for both the summary and the payload.
  const isExclusiveMode = formData.priceEntryMode === PRICE_ENTRY_MODE.EXCLUSIVE_SUBTOTAL;
  const enteredAmount = isExclusiveMode ? formData.subtotal : formData.stayTotalWithVat;
  const breakdown = resolvePriceBreakdown({
    mode: formData.priceEntryMode,
    amount: enteredAmount,
    vatPercentage: formData.vatPercentage
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Initialize form with existing booking data
  useEffect(() => {
    if (booking) {
      // Calculate subtotal from existing total (reverse calculate tax if rate not available)
      const existingSubtotal = booking.rate?.subtotal || (booking.totalAmount / 1.15);

      // Calculate existing VAT percentage if rate breakdown exists
      let existingVatPercentage = 15;  // Default
      // Check if properties exist (not if they're truthy, since tax can be 0)
      if (booking.rate?.subtotal !== undefined && booking.rate?.tax !== undefined) {
        const calculatedVatPercent = booking.rate.subtotal > 0
          ? (booking.rate.tax / booking.rate.subtotal) * 100
          : 0;
        existingVatPercentage = Math.round(calculatedVatPercent);
      }

      setFormData({
        guestName: booking.guestName || '',
        guestEmail: booking.guestEmail || '',
        guestPhone: booking.guestPhone || '',
        checkIn: booking.checkIn ? booking.checkIn.split('T')[0] : '',
        checkOut: booking.checkOut ? booking.checkOut.split('T')[0] : '',
        numberOfGuests: booking.numberOfGuests || 1,
        priceEntryMode: PRICE_ENTRY_MODE.INCLUSIVE_TOTAL,
        // The stored total is the figure the guest agreed to — seed it verbatim.
        stayTotalWithVat: booking.rate?.total ?? booking.totalAmount ?? 0,
        subtotal: Math.round(existingSubtotal * 100) / 100,
        vatPercentage: existingVatPercentage,
        notes: booking.notes || '',
        roomTypeId: booking.roomTypeId || '',
        ratePlanId: booking.ratePlanId || ''
      });
    }
  }, [booking]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.checkIn || !formData.checkOut) {
      setError('يجب تحديد تاريخ الوصول والمغادرة');
      return false;
    }
    if (new Date(formData.checkIn) >= new Date(formData.checkOut)) {
      setError('تاريخ المغادرة يجب أن يكون بعد تاريخ الوصول');
      return false;
    }
    if (!formData.guestName) {
      setError('يجب إدخال اسم الضيف');
      return false;
    }
    if (!(breakdown.total > 0)) {
      setError(isExclusiveMode ? 'يجب إدخال السعر قبل الضريبة' : 'يجب إدخال إجمالي سعر الإقامة');
      return false;
    }
    if (formData.numberOfGuests < 1) {
      setError('عدد الضيوف يجب أن يكون 1 على الأقل');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    // Define variables outside try-catch so they're accessible in catch block
    const token = localStorage.getItem('token');

    // Prepare data for API with calculated values including rate breakdown
    const updateData = {
      ...formData,
      numberOfGuests: parseInt(formData.numberOfGuests),
      totalAmount: breakdown.total,  // Total with VAT (what guest pays)
      rate: {
        subtotal: breakdown.subtotal,  // Pre-VAT amount
        tax: breakdown.tax,            // VAT amount
        total: breakdown.total         // Same as totalAmount
      }
    };

    // Get property ID (handle both object and string)
    const propertyId = booking.property._id || booking.property;

    try {

      const response = await axios.put(
        `/api/v1/properties-legacy/${propertyId}/bookings/${booking._id}`,
        updateData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onHide();
          if (onBookingUpdated) {
            onBookingUpdated();
          }
        }, 1500);
      } else {
        setError(response.data.message || 'حدث خطأ في تحديث الحجز');
      }
    } catch (err) {
      console.error('Error updating booking:', err);

      // Handle availability validation errors
      if (err.response?.status === 409) {
        const conflictType = err.response?.data?.conflictType;
        const canOverride = err.response?.data?.canOverride;
        const errorCode = err.response?.data?.errorCode;
        const errorData = err.response?.data?.errorData || {};

        // Get translated error message
        const translatedError = errorCode
          ? getErrorMessage(errorCode, errorData, 'ar')
          : err.response.data.error;

        // Never allow override when a PERSON is in the room — an existing booking, or a guest
        // relocated into this unit. Same refusal, same next step for the operator.
        if (conflictType === 'existing_booking') {
          setError('❌ ' + translatedError + ' - لا يمكن نقل الحجز لهذه التواريخ.');
          return;
        }
        if (conflictType === 'guest_in_house') {
          setError('❌ ' + translatedError + ' - لا يمكن نقل الحجز لوحدة يوجد بها ضيف.');
          return;
        }

        // For other conflicts, allow override if permitted
        if (canOverride) {
          const confirmOverride = window.confirm(
            `⚠️ تحذير: ${translatedError}\n\n` +
            'هل تريد المتابعة وتحديث الحجز رغم ذلك؟\n' +
            '(هذا الخيار متاح للمدراء فقط)'
          );

          if (confirmOverride) {
            // Retry with forceBooking flag
            try {
              setError(null);
              const retryResponse = await axios.put(
                `/api/v1/properties-legacy/${propertyId}/bookings/${booking._id}`,
                { ...updateData, forceBooking: true },
                {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                }
              );

              if (retryResponse.data.success) {
                setSuccess(true);
                setTimeout(() => {
                  onHide();
                  if (onBookingUpdated) {
                    onBookingUpdated();
                  }
                }, 1500);
              }
            } catch (retryErr) {
              setError(retryErr.response?.data?.message || 'حدث خطأ في تحديث الحجز');
            }
          } else {
            // User chose not to override
            setError(err.response.data.message || err.response.data.error);
          }
        } else {
          // No override allowed
          setError(err.response?.data?.message || err.response?.data?.error || 'حدث خطأ في تحديث الحجز');
        }
      } else {
        // Regular error
        setError(err.response?.data?.message || err.response?.data?.error || 'حدث خطأ في تحديث الحجز');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" dir="rtl">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>تعديل الحجز</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success">
            تم تحديث الحجز بنجاح!
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <h5 className="mb-3">معلومات الضيف</h5>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>اسم الضيف *</Form.Label>
                <Form.Control
                  type="text"
                  name="guestName"
                  value={formData.guestName}
                  onChange={handleChange}
                  required
                  className="touch-input"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>رقم الهاتف</Form.Label>
                <PhoneInput
                  value={formData.guestPhone}
                  defaultCountry="SA"
                  onChange={(phone) => setFormData(prev => ({ ...prev, guestPhone: phone }))}
                  disabled={loading}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>البريد الإلكتروني</Form.Label>
                <Form.Control
                  type="email"
                  name="guestEmail"
                  value={formData.guestEmail}
                  onChange={handleChange}
                  className="touch-input"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>عدد الضيوف *</Form.Label>
                <Form.Control
                  type="number"
                  name="numberOfGuests"
                  value={formData.numberOfGuests}
                  onChange={handleChange}
                  min="1"
                  required
                  className="touch-input"
                />
              </Form.Group>
            </Col>
          </Row>

          <h5 className="mb-3 mt-4">تفاصيل الحجز</h5>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>تاريخ الوصول *</Form.Label>
                <Form.Control
                  type="date"
                  name="checkIn"
                  value={formData.checkIn}
                  onChange={handleChange}
                  required
                  className="touch-input"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>تاريخ المغادرة *</Form.Label>
                <Form.Control
                  type="date"
                  name="checkOut"
                  value={formData.checkOut}
                  onChange={handleChange}
                  required
                  className="touch-input"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3" role="radiogroup" aria-labelledby="edit-price-mode-label">
                <Form.Label id="edit-price-mode-label">طريقة إدخال السعر</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    id="edit-price-mode-inclusive"
                    name="priceEntryMode"
                    label="الإجمالي (شامل الضريبة)"
                    checked={!isExclusiveMode}
                    onChange={() => setFormData(prev => ({
                      ...prev,
                      priceEntryMode: PRICE_ENTRY_MODE.INCLUSIVE_TOTAL
                    }))}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="edit-price-mode-exclusive"
                    name="priceEntryMode"
                    label="السعر قبل الضريبة"
                    checked={isExclusiveMode}
                    onChange={() => setFormData(prev => ({
                      ...prev,
                      priceEntryMode: PRICE_ENTRY_MODE.EXCLUSIVE_SUBTOTAL,
                      // Seed the other basis from the figure on screen, so switching never
                      // resurrects a stale amount the operator can no longer see.
                      subtotal: breakdown.subtotal
                    }))}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>
                  {isExclusiveMode
                    ? 'السعر قبل الضريبة (ريال) *'
                    : 'إجمالي الإقامة شامل الضريبة (ريال) *'}
                </Form.Label>
                <Form.Control
                  type="number"
                  name={isExclusiveMode ? 'subtotal' : 'stayTotalWithVat'}
                  value={enteredAmount}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  required
                  className="touch-input"
                />
                <small className="text-muted">
                  {isExclusiveMode
                    ? 'تُضاف الضريبة فوق هذا المبلغ'
                    : 'المبلغ المتفق عليه مع الضيف كما هو — الضريبة محتسبة بداخله'}
                </small>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>نسبة الضريبة (VAT %)</Form.Label>
                <Form.Select
                  name="vatPercentage"
                  value={formData.vatPercentage}
                  onChange={handleChange}
                  className="touch-select"
                >
                  <option value="15">15% (القيمة المضافة)</option>
                  <option value="0">بدون ضريبة (0%)</option>
                  {/* A booking saved under a rate we no longer offer keeps its own: the
                      percentage here is REVERSE-DERIVED from the stored subtotal/tax, so
                      dropping it from the list would make the field snap to another value
                      and silently rewrite that booking's money on save. */}
                  {!STANDARD_VAT_OPTIONS.includes(Number(formData.vatPercentage)) && (
                    <option value={formData.vatPercentage}>
                      {formData.vatPercentage}% (نسبة محفوظة سابقًا)
                    </option>
                  )}
                </Form.Select>
                <small className="text-muted">
                  {Number(formData.vatPercentage) === 0 ? 'لا توجد ضريبة' : `ضريبة ${formData.vatPercentage}%`}
                </small>
              </Form.Group>
            </Col>
          </Row>

          {/* Price Breakdown Display */}
          <Row>
            <Col md={12}>
              <div className="alert alert-info" dir="rtl">
                <h6 className="mb-2">ملخص السعر:</h6>
                <div className="d-flex justify-content-between">
                  <span>قبل الضريبة:</span>
                  <span>{breakdown.subtotal.toFixed(2)} ريال</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>
                    {Number(formData.vatPercentage) === 0 ? 'الضريبة (بدون):' : `الضريبة (${formData.vatPercentage}%):`}
                  </span>
                  <span>{breakdown.tax.toFixed(2)} ريال</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between">
                  <strong>الإجمالي (شامل الضريبة):</strong>
                  <strong>{breakdown.total.toFixed(2)} ريال</strong>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>ملاحظات</Form.Label>
                <Form.Control
                  as="textarea"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={2}
                  className="touch-input"
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer className="touch-modal-actions">
        <Button variant="secondary" onClick={onHide} disabled={loading} className="touch-btn">
          إلغاء
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading || success}
          className="touch-btn"
        >
          {loading ? 'جاري التحديث...' : 'تحديث الحجز'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditBookingModal;